import { IClient, IUser } from '@api/models';
import { getUserTokenToLoginV4 } from '@api/services/user.service';
import { Button } from '@components/buttons';
import SelectDropdown from '@components/SelectDropDown/SelectDropDown';
import useAuthPermission from '@hooks/use-auth-permission';
import { Table } from '@hyperflake/react-ui-library';
import { ChevronDownIcon, CopyIcon, EllipsisVerticalIcon } from '@icon/index';
import { Dropdown, DropdownItem, DropdownList, DropdownTrigger } from '@library/dropdown';
import { Tooltip } from '@library/tooltip';
import { useMutation } from '@tanstack/react-query';
import RoleChip from 'modules/shared/components/RoleChip/RoleChip';
import StatusChip from 'modules/shared/components/StatusChip/StatusChip';
import { PageHeading, PageTemplate } from 'modules/shared/features/content';
import { useAllUserList } from 'modules/users/hooks/useAllUserList';
import moment from 'moment';
import { useState } from 'react';
import { createSearchParams, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

const AllUsers = () => {
    // const { data, isLoading } = useQuery(['All users'], () => getAllUsers());

    const { allUsers, isLoading, filterOptions, changeFilterValue, filterValue } = useAllUserList();
    const [loggingInUserId, setLoggingInUserId] = useState<string | null>(null);

    // const users = useMemo(() => {
    //     if (!allUser) return [];
    //     return allUser;
    // }, [allUser]);

    const [searchParams, setSsearchParams] = useSearchParams();

    const navigate = useNavigate();
    const { hasPermission } = useAuthPermission();
    const handleCopyDetails = (user: IUser) => {
        let text = `Username:${user.username}\n`;
        text += `Password:${user.password}\n`;
        text += `Sign-in url:${(user.client as IClient).domain}`;

        navigator.clipboard.writeText(text);
        toast.success(`Credentials Copied of ${user.name}`);

        // setHasCopied(true);

        // Change text again agter 3 seconds
        // setTimeout(() => {
        //     setHasCopied(false);
        // }, 2000);
    };

    // const { mutate: loginToV4, isPending: isLoggingIn } = useMutation({
    //     mutationFn: (userId: string) => getUserTokenToLoginV4(userId),
    //     onSuccess: (token) => {
    //         if (!token) {
    //             toast.error('Could not generate login URL');
    //             return;
    //         }
    //         window.open(`${import.meta.env.VITE_APP_REDIRECT_URL}?t=${token}`, '_blank', 'noopener,noreferrer');
    //     },
    //     onError: (e: any) => {
    //         toast.error(e?.message || 'Failed to login to V4');
    //     },
    // });

    // const handleLoginToV4 = (userId: string) => loginToV4(userId);
    const { mutate: loginToV4, isPending: isLoggingIn } = useMutation({
        mutationFn: ({ clientId, userId }: { clientId: string; userId: string }) =>
            getUserTokenToLoginV4(clientId, userId),

        onMutate: ({ userId }) => {
            setLoggingInUserId(userId);
        },

        onSettled: () => {
            setLoggingInUserId(null);
        },

        onSuccess: (token) => {
            if (!token) {
                toast.error('Could not generate login URL');
                return;
            }
            window.open(`${import.meta.env.VITE_APP_REDIRECT_URL}?t=${token}`, '_blank', 'noopener,noreferrer');
        },

        onError: (e: any) => {
            toast.error(e?.message || 'Failed to login to V4');
        },
    });

    return (
        <PageTemplate>
            <div className="flex justify-between">
                <PageHeading>All Users</PageHeading>
                <div>
                    <SelectDropdown
                        className="btn-dropdown btn-secondary btn-secondary-hover "
                        value={filterValue}
                        onChange={changeFilterValue}
                        options={filterOptions}
                        render={(label) => (
                            <span className="flex items-center gap-2 text-grayscale-500 font-medium">
                                View:<span className="text-grayscale-900 font-semibold">{label}</span>{' '}
                                <span>
                                    <ChevronDownIcon className="text-grayscale-400" strokeWidth="2" />
                                </span>
                            </span>
                        )}
                    />
                </div>
            </div>

            <Table
                className="mt-6"
                borderStyle="divider"
                // hover
                isLoading={isLoading}
                data={allUsers}
                columns={[
                    {
                        header: 'ID',
                    },
                    {
                        header: 'Name',
                    },
                    {
                        header: 'Email',
                    },
                    {
                        header: 'Last Active',
                    },
                    {
                        header: 'Role',
                    },
                    {
                        header: 'Status',
                    },
                    {
                        header: 'Action',
                    },
                ]}
                renderRow={(row) => (
                    <Table.Row key={row._id} onClick={() => {}}>
                        <Table.Cell>
                            {
                                <div
                                    className="text-primary cursor-pointer"
                                    // onClick={() => setEditDrawer({ open: true, anchor: 'right', id: row._id })}

                                    onClick={() => {
                                        navigate({
                                            pathname: `/clients/${(row.client as IClient)._id}/users`,
                                            search: createSearchParams({
                                                showDialog: 'true',
                                                userid: row._id,
                                            }).toString(),
                                        });
                                    }}
                                >
                                    {row._id}
                                </div>
                            }
                        </Table.Cell>
                        <Table.Cell>{row.name}</Table.Cell>
                        <Table.Cell>{row.email}</Table.Cell>
                        <Table.Cell>
                            {row.lastActive ? (
                                <div className="whitespace-nowrap">
                                    <div className="">
                                        {moment(row.lastActive, 'YYYYMMDDHHmmss').format('DD MMM YYYY')}
                                    </div>
                                    <div className="">{moment(row.lastActive, 'YYYYMMDDHHmmss').format('HH:mm A')}</div>
                                </div>
                            ) : (
                                '-'
                            )}
                        </Table.Cell>
                        <Table.Cell>{<RoleChip status={row.role} />}</Table.Cell>
                        <Table.Cell>{<StatusChip status={row.status} />}</Table.Cell>
                        <Table.Cell>
                            {
                                <div className="flex items-center gap-4">
                                    <Tooltip placement="top" content={'Login to V4'}>
                                        <Button
                                            size="xs"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                loginToV4({
                                                    clientId: (row.client as IClient)._id,
                                                    userId: row._id,
                                                });
                                            }}
                                            disabled={loggingInUserId === row._id || isLoggingIn}
                                        >
                                            Login
                                        </Button>
                                    </Tooltip>
                                    <Tooltip placement="top" content={'Copy'}>
                                        <button
                                            onClick={() =>
                                                // editUserDailog.show(row)
                                                handleCopyDetails(row)
                                            }
                                        >
                                            <CopyIcon />
                                        </button>
                                    </Tooltip>
                                    <Dropdown placement="bottom-start" showArrow>
                                        <DropdownTrigger className="w-4 h-4 rounded-full">
                                            <EllipsisVerticalIcon />
                                        </DropdownTrigger>

                                        <DropdownList className="min-w-[100px]">
                                            <DropdownItem
                                                onClick={() => {
                                                    navigate({
                                                        pathname: `/clients/${(row.client as IClient)._id}/users/${
                                                            row._id
                                                        }/edit-user`,
                                                        search: createSearchParams({
                                                            showDialog: 'true',
                                                            userid: row._id,
                                                        }).toString(),
                                                    });
                                                }}
                                            >
                                                Edit
                                            </DropdownItem>
                                            {/* <DropdownItem onClick={() => handleLoginToV4(row._id)}>Login</DropdownItem> */}
                                            {/* <DropdownItem className="cursor-default text-slate-200">
                                                    Delete
                                                </DropdownItem> */}
                                        </DropdownList>
                                    </Dropdown>
                                </div>
                            }
                        </Table.Cell>
                    </Table.Row>
                )}
            />
            {/* {!isLoading && allUsers.length === 0 && <EmptyTableTemplate />} */}
            {/* {isLoading && <TableDataLoading />} */}

            {/* </div> */}
            {/* Add drawer */}
            {/* <Drawer
                open={addDrawer.open}
                onClose={() => setAddDrawer({ open: false, anchor: 'right' })}
                anchor={addDrawer.anchor as any}
            >
                <UserAddDrawerForm
                    onClose={(id: string) => {
                        setAccessDrawer({ open: true, anchor: 'right', id });
                        setAddDrawer({ open: false, anchor: 'right' });
                    }}
                />
            </Drawer> */}
            {/* edit drawer */}
            {/* <Drawer
                open={editDrawer.open}
                onClose={() => setEditDrawer({ open: false, anchor: 'right', id: '' })}
                anchor={editDrawer.anchor as any}
            >
                <UserEditDrawerForm
                    userId={editDrawer.id}
                    onClose={() => setEditDrawer({ open: false, anchor: 'right', id: '' })}
                />
            </Drawer> */}
            {/* Project Access Drawer */}
            {/* <Drawer
                open={accessDrawer.open}
                onClose={() => setAccessDrawer({ open: false, anchor: 'right', id: '' })}
                anchor={accessDrawer.anchor as any}
            >
                <ProjectAccessDrawerForm
                    onClose={() => setAccessDrawer({ open: false, anchor: 'right', id: '' })}
                    userId={accessDrawer.id}
                />
            </Drawer> */}
        </PageTemplate>
    );
};

export default AllUsers;
